import React, { useState } from 'react';
import styled from 'styled-components';

const ChecklistContainer = styled.div`
  max-width: 100%;
  padding: 1.5rem;
`;

const ChecklistHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ChecklistTitle = styled.h2`
  margin: 0;
  color: var(--primary-color);
`;

const ChecklistSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  input[type="checkbox"] {
    margin-right: 1rem;
  }

  label {
    flex: 1;
    cursor: pointer;
  }

  .status {
    margin-left: 1rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.8rem;
    background: ${props => props.status === 'completed' ? '#28a745' : props.status === 'pending' ? '#ffc107' : '#dc3545'};
    color: white;
  }
`;

const DeploymentChecklist = () => {
  const [checklist, setChecklist] = useState({
    environment: {
      envVariables: false,
      apiKeys: false,
      secrets: false,
      configFiles: false
    },
    security: {
      ssl: false,
      headers: false,
      csp: false,
      rateLimiting: false
    },
    performance: {
      optimization: false,
      caching: false,
      compression: false,
      cdn: false
    },
    monitoring: {
      logging: false,
      analytics: false,
      errorTracking: false,
      alerts: false
    },
    testing: {
      unitTests: false,
      integrationTests: false,
      securityTests: false,
      accessibilityTests: false
    },
    documentation: {
      apiDocs: false,
      deploymentGuide: false,
      maintenanceGuide: false,
      troubleshootingGuide: false
    }
  });

  const handleCheck = (section, item) => {
    setChecklist(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [item]: !prev[section][item]
      }
    }));
  };

  const getStatus = (completed) => completed ? 'completed' : 'pending';

  const renderSection = (title, section, items) => (
    <ChecklistSection>
      <h3>{title}</h3>
      {Object.entries(items).map(([key, label]) => (
        <ChecklistItem key={key}>
          <input
            type="checkbox"
            id={`${section}-${key}`}
            checked={checklist[section][key]}
            onChange={() => handleCheck(section, key)}
          />
          <label htmlFor={`${section}-${key}`}>{label}</label>
          <span className="status" status={getStatus(checklist[section][key])}>
            {checklist[section][key] ? 'Completed' : 'Pending'}
          </span>
        </ChecklistItem>
      ))}
    </ChecklistSection>
  );

  return (
    <ChecklistContainer>
      <ChecklistHeader>
        <ChecklistTitle>Deployment Checklist</ChecklistTitle>
      </ChecklistHeader>

      {renderSection('Environment Setup', 'environment', {
        envVariables: 'Environment Variables Configured',
        apiKeys: 'API Keys Secured',
        secrets: 'Secrets Management',
        configFiles: 'Configuration Files Updated'
      })}

      {renderSection('Security Measures', 'security', {
        ssl: 'SSL/TLS Certificate',
        headers: 'Security Headers',
        csp: 'Content Security Policy',
        rateLimiting: 'Rate Limiting'
      })}

      {renderSection('Performance Optimization', 'performance', {
        optimization: 'Code Optimization',
        caching: 'Caching Strategy',
        compression: 'Asset Compression',
        cdn: 'CDN Configuration'
      })}

      {renderSection('Monitoring & Analytics', 'monitoring', {
        logging: 'Logging Setup',
        analytics: 'Analytics Integration',
        errorTracking: 'Error Tracking',
        alerts: 'Alert System'
      })}

      {renderSection('Testing', 'testing', {
        unitTests: 'Unit Tests',
        integrationTests: 'Integration Tests',
        securityTests: 'Security Tests',
        accessibilityTests: 'Accessibility Tests'
      })}

      {renderSection('Documentation', 'documentation', {
        apiDocs: 'API Documentation',
        deploymentGuide: 'Deployment Guide',
        maintenanceGuide: 'Maintenance Guide',
        troubleshootingGuide: 'Troubleshooting Guide'
      })}
    </ChecklistContainer>
  );
};

export default DeploymentChecklist; 